import {HttpClient, HttpErrorResponse, HttpParams} from '@angular/common/http';
import {Injectable, inject} from '@angular/core';
import {
    CenterProjection,
    DEFUALT_SEARCH,
    ICenter,
    ICenterActivate,
    ICenterCreate,
    IPagedData,
    IPagedSearch,
    getDefaultpage,
    mongoQueryParser,
} from '@evencel/models';
import {Store} from '@ngxs/store';
import {Observable, catchError, map, of, throwError} from 'rxjs';
import {AppState} from '../../states';

export const CENTER_URL = 'centers';

@Injectable({providedIn: 'root'})
export class CenterService {
    httpClient = inject(HttpClient);
    store = inject(Store);

    post(data: ICenterCreate): Observable<ICenter> {
        return this.httpClient.post<ICenter>(CENTER_URL, data);
    }

    patch(data: ICenter): Observable<ICenter> {
        const url = `${CENTER_URL}/${data._id}`;
        return this.httpClient.patch<ICenter>(url, data);
    }

    activate(data: ICenterActivate): Observable<ICenter> {
        const {active} = data;
        const url = `${CENTER_URL}/${data._id}/activate`;
        return this.httpClient.patch<ICenter>(url, {active});
    }

    delete(id: string): Observable<void> {
        return this.httpClient.delete<void>(`${CENTER_URL}/${id}`);
    }

    restore(id: string): Observable<void> {
        return this.httpClient.delete<void>(`${CENTER_URL}/${id}/restore`);
    }

    private searchParams(data: IPagedSearch) {
        const {active, direction} = data.sort;
        const {pageIndex, pageSize} = data.page;
        let params = new HttpParams();
        params = params.append('pageIndex', `${pageIndex}`);
        params = params.append('pageSize', `${pageSize}`); //
        params = params.append('sort', active);
        params = params.append('order', direction);
        params = params.append('projection', data.projection || 'dataTable');
        return params;
    }

    search<T = ICenter>(data: IPagedSearch): Observable<IPagedData<T>> {
        const params = this.searchParams(data);
        return this.httpClient.post<IPagedData<T>>(`${CENTER_URL}/search`, data.condition, {params}).pipe(
            catchError((e: HttpErrorResponse) => {
                // if the user have no access to the centers then we need to send current center as single item.
                if (e.status === 403) {
                    const paged = getDefaultpage<T>();
                    const center = this.store.selectSnapshot(AppState.user)?.center as T;
                    if (center) {
                        paged.data = [center];
                    }

                    return of(paged);
                }

                return throwError(() => e);
            })
        );
    }

    searchByName<T = ICenter>(acronym: string, projection = CenterProjection.select) {
        const condition = mongoQueryParser.parse({acronym: {$regex: acronym}});
        const action: IPagedSearch = {...DEFUALT_SEARCH, projection, condition};
        return this.search<T>(action).pipe(map(d => d.data));
    }

    getOne(id: string): Observable<ICenter> {
        let params = new HttpParams();
        params = params.append('projection', `form`);
        const url = `${CENTER_URL}/${id}`;
        return this.httpClient.get<ICenter>(url, {params});
    }

    get(data: IPagedSearch): Observable<IPagedData<ICenter>> {
        const params = this.searchParams(data);
        return this.httpClient.get<IPagedData<ICenter>>(CENTER_URL, {params});
    }

    getAll(): Observable<ICenter[]> {
        const url = `${CENTER_URL}`;
        return this.httpClient.get<ICenter[]>(url);
    }
}
